import { useTranslation } from "react-i18next";

import { getCountry } from "@/lib/countries";
import { getLanguage } from "@/lib/languages";
import { getState } from "@/lib/states";
import type { UserProfile } from "@/lib/profile-api";

/**
 * apps/web :: components/dashboard :: CurrentProfileSummary
 *
 * Small card in the dashboard header rail that tells the learner what
 * RoadBridge currently thinks their situation is. Purely presentational;
 * no callbacks, no mutations.
 *
 * Fields shown:
 *   - Home country (English name if in catalog, else raw alpha-2 code).
 *   - Native language (native script, e.g. "తెలుగు").
 *   - Current focus state (English state name).
 *   - CDL interest (yes/no).
 *
 * Unknown catalog codes fall through to raw values so the UI never shows
 * an empty slot. The "change these" link to /settings lives in a follow-up
 * — today users adjust via the state switcher + wizard restart.
 */
export interface CurrentProfileSummaryProps {
  profile: UserProfile;
}

export default function CurrentProfileSummary({
  profile,
}: CurrentProfileSummaryProps) {
  const { t } = useTranslation();

  const country = getCountry(profile.home_country);
  const homeCountryLabel = country?.name ?? profile.home_country;

  const lang = getLanguage(profile.native_language);
  const langLabel = lang?.nativeName ?? profile.native_language;

  const focus = profile.current_focus_state;
  const focusState = focus ? getState(focus) : undefined;
  const focusLabel = focusState?.name ?? focus ?? t("dashboard.profile.noFocus");

  return (
    <section
      aria-labelledby="dashboard-profile-heading"
      data-testid="dashboard-profile-summary"
      className="rounded-lg border border-brand-primary/10 bg-white p-md shadow-sm sm:p-lg"
    >
      <h2
        id="dashboard-profile-heading"
        className="font-display text-sm font-semibold uppercase tracking-wide text-neutral-600"
      >
        {t("dashboard.profile.heading")}
      </h2>
      {/*
        Single-column key/value layout: label flush-left, value flush-right
        on each row. `items-baseline` aligns the small uppercase label with
        the larger value text on a shared baseline. `divide-y` adds subtle
        separators between rows.
      */}
      <dl className="mt-sm divide-y divide-brand-primary/5">
        <div className="flex items-baseline justify-between gap-md py-sm first:pt-0">
          <dt className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
            {t("dashboard.profile.homeCountryLabel")}
          </dt>
          <dd
            data-testid="dashboard-profile-home-country"
            className="text-right text-sm font-medium text-brand-primary"
          >
            {homeCountryLabel}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-md py-sm">
          <dt className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
            {t("dashboard.profile.nativeLanguageLabel")}
          </dt>
          <dd
            data-testid="dashboard-profile-native-language"
            className="text-right text-sm font-medium text-brand-primary"
          >
            {langLabel}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-md py-sm">
          <dt className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
            {t("dashboard.profile.currentStateLabel")}
          </dt>
          <dd
            data-testid="dashboard-profile-current-state"
            className="text-right text-sm font-medium text-brand-primary"
          >
            {focusLabel}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-md py-sm last:pb-0">
          <dt className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
            {t("dashboard.profile.cdlLabel")}
          </dt>
          <dd
            data-testid="dashboard-profile-cdl"
            className="text-right text-sm font-medium text-brand-primary"
          >
            {profile.cdl_interest
              ? t("dashboard.profile.cdlYes")
              : t("dashboard.profile.cdlNo")}
          </dd>
        </div>
      </dl>
    </section>
  );
}
